/**
 * EmailTemplate Form Layout
 *
 * Dual-mode layout:
 * - Standard mode: simplified 2-section form (Template Details, Email Body)
 *   Hides Id, ContentType, EntityType, and other metadata fields.
 * - Admin mode: full form with Id, ContentType, all metadata, and Body.
 */

import React, { useState } from 'react';
import { Box, Text, Grid, Divider, Collapse, UnstyledButton, Group, Code, Table } from '@mantine/core';
import { IconChevronDown, IconChevronRight, IconVariable } from '@tabler/icons-react';
import { getEntityValueFromARef } from '../../../types';
import type { FormAttribute } from '../EntityUpsertForm.types';
import { getAttributeName } from '../../../utils/schemaAttributeUtils';
import { TABLE_SCROLL_MIN_WIDTH } from '../../../theme/mantineTheme';

export interface EmailTemplateFormLayoutProps {
    schemaAttributes: FormAttribute[];
    formData: Record<string, any>;
    onFieldChange: (fieldName: string, value: any) => void;
    renderField: (attr: FormAttribute, value: unknown, onChange: (value: unknown) => void) => React.ReactNode;
    disabled: boolean;
    isStandard?: boolean;
}

const STANDARD_HIDDEN_FIELDS = new Set([
    'id', 'contenttype', 'entitytype',
]);

function normKey(raw: string): string {
    return (raw ?? '').toString().trim().toLowerCase().replace(/^\$\./, '');
}

function findAttr(schemaAttributes: FormAttribute[], key: string): FormAttribute | undefined {
    return schemaAttributes.find(a => {
        const n = normKey((a.name ?? a.id ?? '').toString());
        return n === key;
    });
}

function renderOneField(
    attr: FormAttribute,
    formData: Record<string, any>,
    onFieldChange: (fieldName: string, value: any) => void,
    renderField: EmailTemplateFormLayoutProps['renderField'],
): React.ReactNode {
    const name = getAttributeName(attr);
    const value = name ? getEntityValueFromARef(name, formData as Record<string, unknown>) : '';
    const handleChange = (v: unknown) => onFieldChange(name, v);
    return renderField(attr, value ?? '', handleChange);
}

const VARIABLE_LOCATIONS = [
    { location: 'Requestor', description: 'Attributes of the requesting user/entity' },
    { location: 'Target', description: 'Attributes of the target entity being modified' },
    { location: 'WorkflowData', description: 'Custom data stored during workflow execution' },
    { location: 'RequestApproval', description: 'Attributes of the approval request' },
    { location: 'RequestApprovalType', description: 'Attributes of the approval type configuration' },
    { location: 'Modification', description: 'Computed modification data (change tables, decisions)' },
] as const;

const VARIABLE_EXAMPLES = [
    { variable: '[//Target/DisplayName]', description: 'Display name of the target' },
    { variable: '[//Requestor/Email]', description: 'Email address of the requestor' },
    { variable: '[//Modification/AllChangesAuthorizationTable]', description: 'HTML table of all pending changes' },
    { variable: '[//RequestApproval/Status]', description: 'Current approval status' },
] as const;

function TemplateVariableReference(): React.ReactElement {
    const [open, setOpen] = useState(false);

    return (
        <Box mb="sm" style={{ border: '1px solid var(--mantine-color-default-border)', borderRadius: 'var(--mantine-radius-sm)' }}>
            <UnstyledButton
                onClick={() => setOpen(o => !o)}
                style={{ width: '100%', padding: '8px 12px' }}
            >
                <Group gap="xs">
                    {open ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
                    <IconVariable size={16} color="var(--mantine-color-blue-6)" />
                    <Text size="sm" fw={500}>Available Template Variables</Text>
                </Group>
            </UnstyledButton>
            <Collapse in={open}>
                <Box px="md" pb="sm">
                    <Text size="xs" c="dimmed" mb="xs">
                        Use the syntax <Code>[//Location/AttributeName]</Code> in the Subject or Body fields.
                        Variables are replaced with actual values when the email is sent.
                    </Text>

                    <Text size="xs" fw={600} mt="xs" mb={4}>Locations</Text>
                    <Table.ScrollContainer minWidth={TABLE_SCROLL_MIN_WIDTH}>
                        <Table striped highlightOnHover withTableBorder withColumnBorders fz="xs" mb="sm">
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>Location</Table.Th>
                                    <Table.Th>Description</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {VARIABLE_LOCATIONS.map(v => (
                                    <Table.Tr key={v.location}>
                                        <Table.Td><Code>{v.location}</Code></Table.Td>
                                        <Table.Td>{v.description}</Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>

                    <Text size="xs" fw={600} mb={4}>Examples</Text>
                    <Table.ScrollContainer minWidth={TABLE_SCROLL_MIN_WIDTH}>
                        <Table striped highlightOnHover withTableBorder withColumnBorders fz="xs">
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>Variable</Table.Th>
                                    <Table.Th>Result</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {VARIABLE_EXAMPLES.map(e => (
                                    <Table.Tr key={e.variable}>
                                        <Table.Td><Code>{e.variable}</Code></Table.Td>
                                        <Table.Td>{e.description}</Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>
                </Box>
            </Collapse>
        </Box>
    );
}

function StandardLayout({
    schemaAttributes,
    formData,
    onFieldChange,
    renderField,
}: Omit<EmailTemplateFormLayoutProps, 'isStandard' | 'disabled'>): React.ReactElement {
    const displayNameAttr = findAttr(schemaAttributes, 'displayname');
    const subjectAttr = findAttr(schemaAttributes, 'subject');
    const descriptionAttr = findAttr(schemaAttributes, 'description');
    const bodyAttr = findAttr(schemaAttributes, 'body');

    const knownKeys = new Set(['id', 'displayname', 'subject', 'description', 'contenttype', 'entitytype', 'body']);
    const otherAttrs = schemaAttributes.filter(a => {
        const n = normKey((a.name ?? a.id ?? '').toString());
        return !knownKeys.has(n) && !STANDARD_HIDDEN_FIELDS.has(n);
    });

    const render = (attr: FormAttribute) => renderOneField(attr, formData, onFieldChange, renderField);

    return (
        <Box>
            <Text fw={600} size="sm" c="dimmed" mb={4}>Template Details</Text>
            <Divider mb="sm" />
            {displayNameAttr && render(displayNameAttr)}
            {subjectAttr && (
                <Box mb="xs">
                    {render(subjectAttr)}
                </Box>
            )}
            {descriptionAttr && render(descriptionAttr)}

            {otherAttrs.length > 0 && otherAttrs.map((attr, i) => (
                <Box key={(attr.name ?? attr.id ?? `other-${i}`).toString()}>
                    {render(attr)}
                </Box>
            ))}

            <Text fw={600} size="sm" c="dimmed" mt="md" mb={4}>Email Body</Text>
            <Divider mb="sm" />
            <TemplateVariableReference />
            {bodyAttr && render(bodyAttr)}
        </Box>
    );
}

function AdminLayout({
    schemaAttributes,
    formData,
    onFieldChange,
    renderField,
}: Omit<EmailTemplateFormLayoutProps, 'isStandard' | 'disabled'>): React.ReactElement {
    const idAttr = findAttr(schemaAttributes, 'id');
    const displayNameAttr = findAttr(schemaAttributes, 'displayname');
    const subjectAttr = findAttr(schemaAttributes, 'subject');
    const descriptionAttr = findAttr(schemaAttributes, 'description');
    const contentTypeAttr = findAttr(schemaAttributes, 'contenttype');
    const bodyAttr = findAttr(schemaAttributes, 'body');

    const knownKeys = new Set(['id', 'displayname', 'subject', 'description', 'contenttype', 'body']);
    const otherAttrs = schemaAttributes.filter(a => {
        const n = normKey((a.name ?? a.id ?? '').toString());
        return !knownKeys.has(n);
    });

    const render = (attr: FormAttribute) => renderOneField(attr, formData, onFieldChange, renderField);

    return (
        <Box>
            {idAttr && render(idAttr)}

            <Text fw={600} size="sm" c="dimmed" mb={4}>Template Details</Text>
            <Divider mb="sm" />
            <Grid gutter="md" mb="xs">
                <Grid.Col span={{ base: 12, sm: 6 }}>{displayNameAttr && render(displayNameAttr)}</Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>{subjectAttr && render(subjectAttr)}</Grid.Col>
            </Grid>
            <Grid gutter="md" mb="xs">
                <Grid.Col span={{ base: 12, sm: 4 }}>{contentTypeAttr && render(contentTypeAttr)}</Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>{descriptionAttr && render(descriptionAttr)}</Grid.Col>
            </Grid>

            {otherAttrs.length > 0 && (
                <>
                    <Text fw={600} size="sm" c="dimmed" mt="md" mb={4}>Additional Attributes</Text>
                    <Divider mb="sm" />
                    {otherAttrs.map((attr, i) => (
                        <Box key={(attr.name ?? attr.id ?? `other-${i}`).toString()}>
                            {render(attr)}
                        </Box>
                    ))}
                </>
            )}

            <Text fw={600} size="sm" c="dimmed" mt="md" mb={4}>Email Body</Text>
            <Divider mb="sm" />
            <TemplateVariableReference />
            {bodyAttr && render(bodyAttr)}
        </Box>
    );
}

export const EmailTemplateFormLayout: React.FC<EmailTemplateFormLayoutProps> = ({
    schemaAttributes,
    formData,
    onFieldChange,
    renderField,
    isStandard = false,
}) => {
    if (isStandard) {
        return (
            <StandardLayout
                schemaAttributes={schemaAttributes}
                formData={formData}
                onFieldChange={onFieldChange}
                renderField={renderField}
            />
        );
    }
    return (
        <AdminLayout
            schemaAttributes={schemaAttributes}
            formData={formData}
            onFieldChange={onFieldChange}
            renderField={renderField}
        />
    );
};
